import React, { FC, ReactElement, useState } from 'react'
import MunicipalitySearch, {
  Municipality
} from '@ta-interaktiv/react-municipality-search'
import ChoroplethMap from './choroplethMap'
import customMunicipalities from './Assets/municipalities.json'
export interface Props {
  /** The key of the property you want to compare over the municipalities (e.g. "taxBurden") */
  propertyNameToCompare: string
  /** If the property we are comparing is an array we need a index to what value we want to compare it in the array */
  indexOfPropertyToCompare?: number
  /** The actual data to color the map. Each object must contain the property bfsId! (e.g. [{bfsId: 261, taxBurden: "7"},...]) */
  municipalityData: { bfsId: number; [key: string]: any }[]
  /** Hide the search bar */
  hideMunicipalitySearch?: boolean
  /** The lowest value of your municipalityData  */
  minValue: number
  /** The highest value of your municipalityData  */
  maxValue: number
  /** The dataset used for the municipality search bar. Go to the s3 bucket: interaktiv-tenant-source/static/gemeindesuche/ to see the available datasets (e.g. "2020") */
  municipalitySearchYear?: string
  /** The placeholder of the municipality search bar default is 'Gemeindesuche' and 'Nom de la commune' */
  municipalitySearchPlaceholder?: string
  /** Pass a custom array of municipalities for the municipality search */
  municipalitySearchCustomMunicipalities?: Municipality[]
  /** The year of the topoJson that will be fetched and used to draw the map. Go to https://swiss-maps.vercel.app/ to see the available datasets */
  topoJsonYear: number
  /** If you want to use your own (local) topoJson you can pass it here. Go to https://swiss-maps.vercel.app/ to see the available datasets */
  topoJson?: any
  /** This function gets triggered when a new municipality is selected. The bfsId will be passed to the function (e.g. 261) */
  newActiveMunicipalityIdHandler?: (newId: number) => void
  /** The bfsId of the active municipality */
  activeMunicipalityId?: number
  /** This function gets triggered when the mouse hovers over a municipality. The municipalityData object of the currently hovered municipality will be passed to the function (e.g. {bfsId: 261, taxBurden: "7"}) */
  hoverHandler?: (newData: any) => void
  /** show the zoom controls */
  isZoomable?: boolean
  /** zoom to the currently selected municipality */
  zoomIntoActiveMunicipality?: boolean
  /** distance to the zoomed in municipality */
  zoomDistance?: number
  /** disable touch events on 600px width or smaller */
  disableTouchOnMobile?: boolean
  /** To show a tooltip when hovering you need to pass a tooltip component. The municipalityData object of the currently hovered municipality will be passed as a prop (e.g. {bfsId: 261, taxBurden: "7"}) */
  ToolTip?: FC<ToolTipProps>
  /** The colorpalette of 3 colors (low, mid, high) for the choropleth map. You can use color names or hex (e.g. ['green', 'lightgrey', '#ff0000']) */
  colorPalette?: [string, string, string]
  /** stroke-width of active municipality */
  activeStrokeWidth?: string
  /** stroke-color of active municipality */
  activeStrokeColor?: string
  /** stroke-width of hovered municipality */
  hoverStrokeWidth?: string
  /** stroke-color of hovered municipality */
  hoverStrokeColor?: string
  /** stroke-color of all municipalities */
  municipalityStrokeColor?: string
  /** stroke-width of all municipalities */
  municipalityStrokeWidth?: string
  /** fill of all lakes */
  lakeFillColor?: string
  /** stroke-color of all lakes */
  lakeStrokeColor?: string
  /** stroke-width of all lakes */
  lakeStrokeWidth?: string
  /** stroke-color national border */
  nationalBorderColor?: string
  /** stroke-width national border */
  nationalBorderWidth?: string
  /** stroke-color of all cantons */
  cantonStrokeColor?: string
  /** stroke-width of all cantons */
  cantonStrokeWidth?: string
  /** lang of component */
  locale?: 'de' | 'fr'

  /** custom props for this story */
  currentPrice: number
  currentRoomSize: string
  /** event handler for fullscreen state change */
  fullscreenChangeHandler?: (isFullscreen: boolean) => void
  /** is the map in fullscreen mode or not */
  isFullscreen?: boolean
  /** custom legend component */
  LegendComponent?: FC
  /** scroll map into view when new municipality is selected */
  scrollIntoView?: boolean
}

export const SwissMap: FC<Props> = (
  {
  municipalityData,
  propertyNameToCompare,
  indexOfPropertyToCompare,
  hideMunicipalitySearch = false,
  minValue,
  maxValue,
  municipalitySearchYear,
  municipalitySearchPlaceholder,
  municipalitySearchCustomMunicipalities,
  topoJsonYear = 2022,
  topoJson,
  newActiveMunicipalityIdHandler,
  activeMunicipalityId = 0,
  hoverHandler,
  isZoomable = true,
  zoomIntoActiveMunicipality = false,
  zoomDistance = 40,
  disableTouchOnMobile,
  ToolTip,
  colorPalette,
  activeStrokeWidth = '2',
  activeStrokeColor = '#ffff',
  hoverStrokeWidth = '2',
  hoverStrokeColor = '#ffff',
  municipalityStrokeColor = '#aaa',
  municipalityStrokeWidth = '0.5',
  lakeFillColor = 'rgb(239, 236, 224)',
  lakeStrokeColor = 'none',
  lakeStrokeWidth = '0',
  nationalBorderColor = 'none',
  nationalBorderWidth = '0',
  cantonStrokeColor = 'rgba(0,0,0,0.4)',
  cantonStrokeWidth = '1',
  locale = 'de',
  currentPrice,
  currentRoomSize,
  fullscreenChangeHandler,
  isFullscreen,
  LegendComponent,
  scrollIntoView
} 
): ReactElement => {
  const [toolTipData, setToolTipData] = useState<any | null>()
  // toolTipRef
  const toolTipRef = React.useRef<HTMLDivElement>(null)
  const toolTipStyle = {
    pointerEvents: "none" as "none",
    position: "fixed" as "fixed",
    opacity: 0,
    transition: "opacity 0.3s ease-in-out",
    zIndex: 102,
  }
  
  return (
    <div>

      {!hideMunicipalitySearch && municipalitySearchYear && (
        <MunicipalitySearch
          customMunicipalities={customMunicipalities}
          locale={locale}
          dedupe
          hideTooltip
          resetOnSelect
          placeholder={municipalitySearchPlaceholder ?? (locale === 'fr' ? 'Nom de la Commune' : 'Gemeindesuche')}
          onSelectionHandler={(selectedMunicipality: Municipality) => {
            if (newActiveMunicipalityIdHandler) {
              newActiveMunicipalityIdHandler(selectedMunicipality.GDENR)
            }
          }}
        />
      )}
      {LegendComponent && <LegendComponent />}
      <ChoroplethMap
        municipalityData={municipalityData}
        propertyNameToCompare={propertyNameToCompare}
        indexOfPropertyToCompare={indexOfPropertyToCompare}
        minValue={minValue}
        maxValue={maxValue}
        year={topoJsonYear}
        topoJson={topoJson}
        activeMunicipalityId={activeMunicipalityId}
        newActiveMunicipalityIdHandler={(newId: number) => {
          if (newActiveMunicipalityIdHandler) {
            newActiveMunicipalityIdHandler(newId)
          }
        }}
        hoverHandler={newData => {
          if (hoverHandler) {
            hoverHandler(newData)
          }
          if (ToolTip) {
            setToolTipData(newData)
          }
        }}
        isZoomable={isZoomable}
        zoomIntoActiveMunicipality={zoomIntoActiveMunicipality}
        zoomDistance={zoomDistance}
        disableTouchOnMobile={disableTouchOnMobile}
        colorPalette={colorPalette}
        activeStrokeWidth={activeStrokeWidth}
        activeStrokeColor={activeStrokeColor}
        hoverStrokeWidth={hoverStrokeWidth}
        hoverStrokeColor={hoverStrokeColor}
        municipalityStrokeColor={municipalityStrokeColor}
        municipalityStrokeWidth={municipalityStrokeWidth}
        lakeFillColor={lakeFillColor}
        lakeStrokeColor={lakeStrokeColor}
        lakeStrokeWidth={lakeStrokeWidth}
        nationalBorderColor={nationalBorderColor}
        nationalBorderWidth={nationalBorderWidth}
        cantonStrokeColor={cantonStrokeColor}
        cantonStrokeWidth={cantonStrokeWidth}
        toolTipRef={toolTipRef}
        municipalityFallbackFillColor='#737373'
        fullscreenChangeHandler={fullscreenChangeHandler}
        isFullscreen={isFullscreen}
        fullscreenHint={locale === 'fr' ? 'Plein écran' : 'Vollbild'}
        scrollIntoView={scrollIntoView}
      />
      {ToolTip && (
        <div id='tooltip' style={ toolTipStyle } ref={toolTipRef}>
          <ToolTip activeData={toolTipData} locale={locale} currentPrice={currentPrice} currentRoomSize={currentRoomSize}  />
        </div>
      )}
    </div>
  )
}
